import React, { useRef, useState } from 'react';
import { styles } from './SearchBar.css';
import { SearchIcon } from '@jbhi-fi/lanyard-ui/components/Icons/Search';
import CloseIcon from '@jbhi-fi/lanyard-ui/components/Icons/Close';
import LoadingIcon from '@jbhi-fi/lanyard-ui/components/Icons/Loading';
import TextField, { AutoCompleteType } from '@jbhi-fi/lanyard-ui/components/TextField';

export interface SearchBarResult {
  title: string;
  onClick?: () => void;
}

export interface SearchBarProps {
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onSearch?: (query: string) => void;
  isLoading?: boolean;
  hasFetchedResults?: boolean;
  results?: Array<SearchBarResult>;
  maxResults?: number;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlight = (text: string, query: string) => {
  if (!query) return text;
  const escaped = escapeRegExp(query);
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? (
      <span className={styles.highlight} key={i}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

export const SearchBar = ({
  placeholder,
  value,
  onChange,
  onClear,
  onSearch,
  results,
  isLoading,
  hasFetchedResults,
  maxResults = 100,
}: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasNoResults = hasFetchedResults && value?.length && !results?.length;

  const handleResultClick = (result: SearchBarResult) => {
    result.onClick?.();
    inputRef.current?.blur();
  };

  const handleSearch = (query: string) => {
    inputRef.current?.blur();
    onSearch?.(query);
  };

  return (
    <div className={styles.container}>
      <TextField
        id="help-and-support-search"
        name="help-and-support-search"
        inputRef={inputRef}
        icon={<SearchIcon />}
        label={placeholder}
        autoComplete={AutoCompleteType.off}
        value={value}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSearch(value || '');
            e.preventDefault();
          }
        }}
      />
      {isLoading ? <LoadingIcon className={styles.rightIcon} width={20} height={20} /> : null}
      {onClear && !isLoading && value?.length ? <CloseIcon className={styles.rightIcon} onClick={onClear} /> : null}
      {isOpen ? (
        <>
          {results?.length ? (
            <div className={styles.resultsContainer}>
              {results.slice(0, maxResults).map(result => (
                <button
                  key={result.title}
                  className={styles.result}
                  onClick={() => handleResultClick(result)}
                  onMouseDown={e => e.preventDefault()}
                >
                  {highlight(result.title, value || '')}
                </button>
              ))}
              {results.length > maxResults && onSearch ? (
                <button
                  className={styles.viewAll}
                  onClick={() => handleSearch(value || '')}
                  onMouseDown={e => e.preventDefault()}
                >
                  View all results
                </button>
              ) : null}
            </div>
          ) : null}
          {hasNoResults ? (
            <div className={styles.resultsContainer}>
              <button className={styles.result}>No results found</button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
